/* eslint-disable */
import { EnumLike, EnumReflection, SchemaOfObject } from '@salsita-ai/platform-project-sdk/domain'

export const ApplicationVersion = '0.0.0'

export type PublishedData = {
  readonly customMaterials?: {
    readonly id: string
    readonly code: string
    readonly name: string
    readonly length: number
    readonly width: number
    readonly thickness: number
    readonly rawMaterial: 'brass' | 'ss304' | 'ss316' | 'aluminium'
    readonly finish: 'brushed' | 'milled' | 'chequer' | 'propeller'
    readonly standard: boolean
    readonly powderCoatingEnabled: boolean
  }[]
  readonly countersinks?: {
    readonly id: string
    readonly topRadius: number
    readonly bottomRadius: number
    readonly plateThicknessMin: number
    readonly plateThicknessMax: number
  }[]
  readonly featureSettings?: {
    readonly id: string
    readonly plateType: 'RoundPlate' | 'SquarePlate' | 'RectangularPlate' | 'KickPlate' | 'StrikePlate'
    readonly featureType:
      | 'BoltHole'
      | 'CamlockHole'
      | 'CornerBoltHoles'
      | 'CornerCountersinkHoles'
      | 'CountersinkHole'
      | 'DeadboltHole'
      | 'EuroCylinderHoleAu'
      | 'EuroCylinderHoleEu'
      | 'Hole54'
      | 'KickPlateBoltHoles'
      | 'KickPlateCountersinkHoles'
      | 'OvalCylinderHole'
      | 'PD7Hole'
      | 'PlainCylinderHole'
      | 'PlainLocksetHole'
      | 'PN6Hole'
      | 'RectangularHole'
      | 'RimCylinderHole'
      | 'SquareHole'
    readonly anchorPointPosition:
      | 'topLeft'
      | 'top'
      | 'topRight'
      | 'right'
      | 'bottomRight'
      | 'bottom'
      | 'bottomLeft'
      | 'left'
      | 'center'
    readonly offsetX: number
    readonly offsetY: number
  }[]
  readonly presetCategories?: {
    readonly id: string
    readonly name: string
    readonly description?: string
    readonly img?: string
    readonly imgAlt?: string
    readonly presets: {
      readonly configurationId: string
      readonly revisionId: string
      readonly readToken: string
      readonly name: string
      readonly description: string
      readonly img?: string
      readonly imgAlt?: string
      readonly detailTitle?: string
      readonly detailDescription?: string
      readonly tags?: readonly string[]
    }[]
  }[]
  readonly recipes?: {
    readonly id: string
    readonly name: string
    readonly description: string
    readonly collection: string
    readonly configuration: {
      readonly modelVersion: number
      readonly entities: {
        readonly id: string
        readonly type: string
        [k: string]: unknown
      }[]
      readonly rootEntityType: string
      readonly recipeIds: readonly string[]
    }
  }[]
}

export const PublishedData = {
  type: 'object',
  properties: {
    customMaterials: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          code: { type: 'string' },
          name: { type: 'string' },
          length: { type: 'number' },
          width: { type: 'number' },
          thickness: { type: 'number' },
          rawMaterial: { type: 'string', enum: ['brass', 'ss304', 'ss316', 'aluminium'] },
          finish: { type: 'string', enum: ['brushed', 'milled', 'chequer', 'propeller'] },
          standard: { type: 'boolean' },
          powderCoatingEnabled: { type: 'boolean' },
        },
        additionalProperties: false,
        required: [
          'id',
          'code',
          'name',
          'length',
          'width',
          'thickness',
          'rawMaterial',
          'finish',
          'standard',
          'powderCoatingEnabled',
        ],
      },
    },
    countersinks: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          topRadius: { type: 'number', minimum: 0 },
          bottomRadius: { type: 'number', minimum: 0 },
          plateThicknessMin: { type: 'number', minimum: 0 },
          plateThicknessMax: { type: 'number', minimum: 0 },
        },
        additionalProperties: false,
        required: ['id', 'topRadius', 'bottomRadius', 'plateThicknessMin', 'plateThicknessMax'],
      },
    },
    featureSettings: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          plateType: {
            type: 'string',
            enum: ['RoundPlate', 'SquarePlate', 'RectangularPlate', 'KickPlate', 'StrikePlate'],
          },
          featureType: {
            type: 'string',
            enum: [
              'BoltHole',
              'CamlockHole',
              'CornerBoltHoles',
              'CornerCountersinkHoles',
              'CountersinkHole',
              'DeadboltHole',
              'EuroCylinderHoleAu',
              'EuroCylinderHoleEu',
              'Hole54',
              'KickPlateBoltHoles',
              'KickPlateCountersinkHoles',
              'OvalCylinderHole',
              'PD7Hole',
              'PlainCylinderHole',
              'PlainLocksetHole',
              'PN6Hole',
              'RectangularHole',
              'RimCylinderHole',
              'SquareHole',
            ],
          },
          anchorPointPosition: {
            type: 'string',
            enum: ['topLeft', 'top', 'topRight', 'right', 'bottomRight', 'bottom', 'bottomLeft', 'left', 'center'],
          },
          offsetX: { type: 'number' },
          offsetY: { type: 'number' },
        },
        additionalProperties: false,
        required: ['id', 'plateType', 'featureType', 'anchorPointPosition', 'offsetX', 'offsetY'],
      },
    },
    presetCategories: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          name: { type: 'string' },
          description: { type: 'string' },
          img: { type: 'string' },
          imgAlt: { type: 'string' },
          presets: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                configurationId: { type: 'string' },
                revisionId: { type: 'string' },
                readToken: { type: 'string' },
                name: { type: 'string' },
                description: { type: 'string' },
                img: { type: 'string' },
                imgAlt: { type: 'string' },
                detailTitle: { type: 'string' },
                detailDescription: { type: 'string' },
                tags: { type: 'array', items: { type: 'string' } },
              },
              required: ['configurationId', 'revisionId', 'readToken', 'name', 'description'],
              additionalProperties: false,
            },
          },
        },
        required: ['id', 'name', 'presets'],
        additionalProperties: false,
      },
    },
    recipes: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          name: { type: 'string' },
          description: { type: 'string' },
          collection: { type: 'string' },
          configuration: {
            type: 'object',
            properties: {
              modelVersion: { type: 'number' },
              entities: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: { id: { type: 'string' }, type: { type: 'string' } },
                  required: ['id', 'type'],
                  additionalProperties: true,
                },
              },
              rootEntityType: { type: 'string' },
              recipeIds: { type: 'array', items: { type: 'string' } },
            },
            required: ['modelVersion', 'entities', 'rootEntityType', 'recipeIds'],
            additionalProperties: false,
          },
        },
        required: ['id', 'name', 'description', 'collection', 'configuration'],
        additionalProperties: false,
      },
    },
  },
  required: [],
  additionalProperties: false,
} as SchemaOfObject<PublishedData>

export type SessionData = {
  readonly accessToken: string
}

export const SessionData = {
  type: 'object',
  properties: { accessToken: { type: 'string' } },
  required: ['accessToken'],
  additionalProperties: false,
} as SchemaOfObject<SessionData>

export type FrontendData = {
  [k: string]: unknown
}

export const FrontendData = {} as SchemaOfObject<FrontendData>

export type FinalizeData = {
  readonly accessToken: string
  readonly stepFile: string
  readonly quoteId: number
  readonly quoteLineId: number | null
  readonly usage: string
  readonly materialCode: string
  readonly rawMaterial: string
  readonly usedCountersinks: readonly string[]
  readonly volume: number
}

export const FinalizeData = {
  type: 'object',
  properties: {
    accessToken: { type: 'string' },
    stepFile: { type: 'string' },
    quoteId: { type: 'number' },
    quoteLineId: { anyOf: [{ type: 'number' }, { type: 'null' }] },
    usage: { type: 'string' },
    materialCode: { type: 'string' },
    rawMaterial: { type: 'string' },
    usedCountersinks: { type: 'array', items: { type: 'string' } },
    volume: { type: 'number', minimum: 0 },
  },
  required: [
    'accessToken',
    'stepFile',
    'quoteId',
    'quoteLineId',
    'usage',
    'materialCode',
    'rawMaterial',
    'usedCountersinks',
    'volume',
  ],
  additionalProperties: false,
} as SchemaOfObject<FinalizeData>

export type PrefetchedFinalizeData = {
  [k: string]: unknown
}

export const PrefetchedFinalizeData = {} as SchemaOfObject<PrefetchedFinalizeData>

export type FinalizeResult = {}

export const FinalizeResult = {
  type: 'object',
  properties: {},
  required: [],
  additionalProperties: false,
} as SchemaOfObject<FinalizeResult>

export type CallbackData = {
  readonly accessToken: string
  readonly quoteId: number
  readonly quoteLineId: number | null
  readonly data: {
    readonly configurationId: string
    readonly stepFile: string
    readonly quantity: number
    readonly usage: string
    readonly designSpec: {
      readonly height: number
      readonly width: number
      readonly material: string
      readonly holes: {
        [k: string]: number
      }
      readonly numBends: number
      readonly powderCoating: boolean
      readonly handing: string
      readonly thickness: string
      readonly volume: string
      readonly materialName: string
      readonly shape: string
      readonly finish: string
    }
  }
}

export const CallbackData = {
  type: 'object',
  properties: {
    accessToken: { type: 'string' },
    quoteId: { type: 'number' },
    quoteLineId: { anyOf: [{ type: 'number' }, { type: 'null' }] },
    data: {
      type: 'object',
      properties: {
        configurationId: { type: 'string' },
        stepFile: { type: 'string' },
        quantity: { type: 'number' },
        usage: { type: 'string' },
        designSpec: {
          type: 'object',
          properties: {
            height: { type: 'number' },
            width: { type: 'number' },
            material: { type: 'string' },
            holes: { additionalProperties: { type: 'number', minimum: 0 } },
            numBends: { type: 'number' },
            powderCoating: { type: 'boolean' },
            handing: { type: 'string' },
            thickness: { type: 'string' },
            volume: { type: 'string' },
            materialName: { type: 'string' },
            shape: { type: 'string' },
            finish: { type: 'string' },
          },
          additionalProperties: false,
          required: [
            'height',
            'width',
            'material',
            'holes',
            'numBends',
            'powderCoating',
            'handing',
            'thickness',
            'volume',
            'materialName',
            'shape',
            'finish',
          ],
        },
      },
      additionalProperties: false,
      required: ['configurationId', 'stepFile', 'quantity', 'usage', 'designSpec'],
    },
  },
  required: ['accessToken', 'quoteId', 'quoteLineId', 'data'],
  additionalProperties: false,
} as SchemaOfObject<CallbackData>

export type BackendData = never

export const BackendData = undefined

export type NavigationState =
  | 'Dimensions'
  | 'Features'
  | 'FeatureList'
  | 'Summary'
  | 'BulkFinalization'
  | 'Finalization'
  | 'FinalizationThumbnail'

export const NavigationState: EnumLike<NavigationState> = new EnumReflection('NavigationState', {
  enum: [
    'Dimensions',
    'Features',
    'FeatureList',
    'Summary',
    'BulkFinalization',
    'Finalization',
    'FinalizationThumbnail',
  ],
}) as EnumLike<NavigationState>

export type ViewState = '3d' | '2d'

export const ViewState: EnumLike<ViewState> = new EnumReflection('ViewState', {
  enum: ['3d', '2d'],
}) as EnumLike<ViewState>

export type ToolState = 'Configure' | 'Measure'

export const ToolState: EnumLike<ToolState> = new EnumReflection('ToolState', {
  enum: ['Configure', 'Measure'],
}) as EnumLike<ToolState>

export type AdminNavigationState = 'PresetCategories' | 'Presets' | 'Materials' | 'Countersinks' | 'FeatureSettings'

export const AdminNavigationState: EnumLike<AdminNavigationState> = new EnumReflection('AdminNavigationState', {
  enum: ['PresetCategories', 'Presets', 'Materials', 'Countersinks', 'FeatureSettings'],
}) as EnumLike<AdminNavigationState>

export type AdminViewState = 'Default'

export const AdminViewState: EnumLike<AdminViewState> = new EnumReflection('AdminViewState', {
  enum: ['Default'],
}) as EnumLike<AdminViewState>
